import { paymentProvider, displayedPaymentMethod } from "./payment-provider";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  fetchTotalWeightKg,
  mrpandaShippingCost,
  eltaCostByWeight,
  boxnowCostByWeight,
  skroutzCostByWeight,
  acsCostByWeight,
  courierCenterCostByWeight,
} from "./shipping.functions";
import { resolveCarrierName } from "./notify-ops.functions";
import { reducedVatRate } from "./island-vat";
import { uniqueOrderNumber } from "./order-number";
import { isMobilePhone, normalizePhoneDigits, isValidContactPhone } from "./phone";
import { orderNotesSchema } from "./order-notes";

type Json = string | number | boolean | null | Json[] | { [k: string]: Json };

const itemSchema = z.object({
  productId: z.string().min(1),
  name: z.string().min(1),
  price: z.number().nonnegative(),
  quantity: z.number().int().min(1).max(999),
  imageUrl: z.string().nullable().optional(),
  // Αν η γραμμή ανήκει σε MrBox πακέτο, το id του πακέτου. Ο server
  // υπολογίζει μόνος του το ποσοστό/ποσό έκπτωσης — ποτέ ο client.
  bundleId: z.string().max(60).nullable().optional(),
  bundleKey: z.string().max(80).nullable().optional(),
});

const addressSchema = z.object({
  line1: z.string().min(1).max(200),
  line2: z.string().max(200).optional().default(""),
  city: z.string().min(1).max(100),
  region: z.string().max(100).optional().default(""),
  postalCode: z.string().min(3).max(20),
  country: z.string().min(2).max(60).default("Ελλάδα"),
  vatNumber: z.string().max(20).optional().default(""),
  // Τ.Κ. έδρας του ΑΦΜ (από την ΑΑΔΕ) — χρησιμοποιείται για τον έλεγχο
  // μειωμένου ΦΠΑ νησιών ανεξάρτητα από τον Τ.Κ. παράδοσης.
  vatPostalCode: z.string().max(20).optional().default(""),
});

const createOrderSchema = z.object({
  email: z.string().email().max(200),
  fullName: z.string().min(2).max(120),
  phone: z
    .string()
    .trim()
    .refine((v) => isValidContactPhone(v), {
      message:
        "Δώσε έγκυρο τηλέφωνο: κινητό Ελλάδας 69…, σταθερό 2…/8… ή κυπριακό 9XXXXXXX / 2XXXXXXX.",
    }),
  notes: orderNotesSchema.optional().default(""),
  shippingAddress: addressSchema,
  items: z.array(itemSchema).min(1).max(100),
  paymentMethod: z.enum(["cash", "bank_transfer", "iris", "klarna", "viva_card", "viva"]),
  shippingMethod: z.string().max(60).optional(),
  shippingMethodLabel: z.string().max(120).optional(),
  documentType: z.enum(["receipt", "invoice"]).optional(),
  outOfStockAction: z.string().max(120).optional(),
  // Optional discount coupon. The server re-validates this against the ops
  // coupon API and recomputes the authoritative discount — the client value is
  // never trusted.
  couponCode: z.string().trim().max(40).optional(),
  // Optional MrBox bundle ids present in the cart. The server rebuilds each
  // bundle and recomputes the authoritative bundle discount — the client value
  // is never trusted for the amount.
  bundleIds: z.array(z.string().max(60)).max(20).optional(),
  // Structured locker / pickup-point selection (BoxNow or Skroutz Last Mile).
  // Kept alongside the free-text note so ops can read the locker id directly.
  pickupPoint: z
    .object({
      provider: z.enum(["boxnow", "skroutz"]),
      id: z.string().min(1).max(60),
      name: z.string().max(200).optional().default(""),
      address: z.string().max(300).optional().default(""),
      postcode: z.string().max(20).optional().default(""),
    })
    .optional(),
  invoice: z
    .object({
      company: z.string().max(200).optional().default(""),
      doy: z.string().max(120).optional().default(""),
      profession: z.string().max(200).optional().default(""),
    })
    .optional(),
});

function computeTotals(items: { price: number; quantity: number }[]) {
  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const shipping = subtotal > 0 && subtotal < 50 ? 3.5 : 0;
  return {
    subtotal: Math.round(subtotal * 100) / 100,
    shipping,
    total: Math.round((subtotal + shipping) * 100) / 100,
  };
}

const round2 = (n: number) => Math.round(n * 100) / 100;

type AdminClient = (typeof import("@/integrations/supabase/client.server"))["supabaseAdmin"];

function toNum(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const t = v.trim();
    if (!t) return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

// Imported (PrestaShop / Skroutz) orders keep the original payment label inside
// raw.module / raw.payment but leave the structured payment_method column empty.
// Map those free-text labels onto our internal payment methods for display.
function derivePaymentMethod(raw: Record<string, unknown> | null | undefined): string | null {
  if (!raw) return null;
  const hay = `${typeof raw.module === "string" ? raw.module : ""} ${
    typeof raw.payment === "string" ? raw.payment : ""
  }`.toLowerCase();
  if (!hay.trim()) return null;
  if (/klarna/.test(hay)) return "klarna";
  if (/iris/.test(hay)) return "iris";
  if (/viva|κάρτα|card|πιστωτ|smart\s*checkout/.test(hay)) return "viva_card";
  if (/cash|cod|αντικαταβολ|μετρητ/.test(hay)) return "cash";
  if (/bank|τραπεζ|έμβασμα|εμβασμα|transfer|deposit/.test(hay)) return "bank_transfer";
  return null;
}

// Fill in the display fields imported orders leave empty, using the values
// preserved inside the order's raw payload. New eshop orders always store a
// subtotal, so they are detected as non-legacy and returned untouched.
function deriveLegacyOrderFields(
  order: {
    subtotal: unknown;
    shipping_cost: unknown;
    total: unknown;
    payment_method: unknown;
    payment_status: unknown;
    status: unknown;
  },
  raw: Record<string, unknown> | null | undefined,
): {
  subtotal: number | null;
  shipping_cost: number | null;
  payment_method: string | null;
  payment_status: string;
} {
  const total = toNum(order.total);
  let subtotal = toNum(order.subtotal);
  let shipping = toNum(order.shipping_cost);
  let paymentMethod =
    typeof order.payment_method === "string" && order.payment_method ? order.payment_method : null;
  let paymentStatus = typeof order.payment_status === "string" ? order.payment_status : "";
  const status = typeof order.status === "string" ? order.status : "";

  // New eshop orders always persist a subtotal; a missing one marks an import.
  const isLegacy = subtotal == null;
  if (isLegacy && raw) {
    subtotal = toNum(raw.total_products_wt) ?? toNum(raw.total_products);
    const rawShip = toNum(raw.total_shipping_tax_incl) ?? toNum(raw.total_shipping);
    if (rawShip != null) shipping = rawShip;
    if (subtotal == null && total != null) subtotal = round2(total - (shipping ?? 0));
  }

  if (!paymentMethod) paymentMethod = derivePaymentMethod(raw);

  // Imported orders are historical: if the original payload shows money was paid
  // (or the order was already shipped/delivered/completed) treat it as paid so
  // it is not mistaken for a new, unpaid online order.
  if (isLegacy && paymentStatus !== "paid" && paymentStatus !== "refunded") {
    const paidReal = toNum(raw?.total_paid_real);
    const looksPaid =
      (paidReal != null && paidReal > 0) ||
      ["shipped", "delivered", "completed"].includes(status);
    if (looksPaid && status !== "cancelled") paymentStatus = "paid";
  }

  return { subtotal, shipping_cost: shipping, payment_method: paymentMethod, payment_status: paymentStatus };
}

// Imported order_items carry the product SKU/barcode but no image. Look the
// matching products up in the catalog and fill in their image so
// imported orders render with real photos like eshop orders. Mutates in place.
async function backfillItemImages(
  supabaseAdmin: AdminClient,
  items: Array<Record<string, unknown>>,
): Promise<void> {
  const codes = new Set<string>();
  for (const it of items) {
    if (it.image_url) continue;
    const sku = typeof it.sku === "string" ? it.sku.trim() : "";
    const barcode = typeof it.barcode === "string" ? it.barcode.trim() : "";
    if (sku) codes.add(sku);
    if (barcode) codes.add(barcode);
  }
  if (codes.size === 0) return;
  const list = Array.from(codes);
  const map = new Map<string, string | null>();
  const add = (rows: unknown[] | null | undefined) => {
    for (const r of rows ?? []) {
      const p = r as {
        reference: string | null;
        ean13: string | null;
        upc: string | null;
        image_url: string | null;
      };
      for (const key of [p.reference, p.ean13, p.upc]) {
        if (typeof key === "string" && key.trim()) map.set(key.trim(), p.image_url ?? null);
      }
    }
  };
  const cols = "reference, ean13, upc, image_url";
  const [byRef, byEan, byUpc] = await Promise.all([
    supabaseAdmin.from("products").select(cols).in("reference", list),
    supabaseAdmin.from("products").select(cols).in("ean13", list),
    supabaseAdmin.from("products").select(cols).in("upc", list),
  ]);
  add(byRef.data as unknown[] | null);
  add(byEan.data as unknown[] | null);
  add(byUpc.data as unknown[] | null);
  for (const it of items) {
    if (it.image_url) continue;
    const sku = typeof it.sku === "string" ? it.sku.trim() : "";
    const barcode = typeof it.barcode === "string" ? it.barcode.trim() : "";
    const img = (sku && map.get(sku)) || (barcode && map.get(barcode));
    if (img) it.image_url = img;
  }
}

// SECURITY: the shipping cost MUST be recomputed server-side from the selected
// shipping method and the (authoritative) cart — never trusted from the client.
// Mirrors the exact pricing used by the shipping.functions.ts server functions
// that the checkout UI calls to display each courier's price.
async function recomputeShipping(
  method: string | undefined,
  ctx: {
    items: { productId: string; quantity: number }[];
    subtotal: number;
    address: { line1: string; city: string; postalCode: string; country?: string };
    fallback: number;
  },
): Promise<number> {
  const round2 = (n: number) => Math.round(n * 100) / 100;
  switch (method) {
    // No shipping charge: customer picks up, or arranges & pays freight directly.
    case "store_pickup":
    case "customer_freight":
      return 0;
    case "mrpanda_delivery": {
      const r = await mrpandaShippingCost({
        line1: ctx.address.line1,
        city: ctx.address.city,
        postalCode: ctx.address.postalCode,
        subtotal: ctx.subtotal,
      });
      return r.serviceable ? round2(r.cost) : ctx.fallback;
    }
    case "elta_courier": {
      const w = await fetchTotalWeightKg(ctx.items);
      return round2(eltaCostByWeight(ctx.address.city, w).cost);
    }
    case "acs": {
      const w = await fetchTotalWeightKg(ctx.items);
      return round2((await acsCostByWeight(ctx.address.postalCode, w)).cost);
    }
    case "courier_center": {
      const w = await fetchTotalWeightKg(ctx.items);
      return round2(
        courierCenterCostByWeight(ctx.address.postalCode, ctx.address.country, w).cost,
      );
    }
    case "boxnow": {
      const w = await fetchTotalWeightKg(ctx.items);
      const r = boxnowCostByWeight(ctx.address.postalCode, w);
      return r.serviceable ? round2(r.cost) : ctx.fallback;
    }
    case "skroutz_last_mile": {
      const w = await fetchTotalWeightKg(ctx.items);
      const r = skroutzCostByWeight(ctx.address.postalCode, w);
      return r.serviceable ? round2(r.cost) : ctx.fallback;
    }
    default:
      // Unknown / unset method → flat estimate (never the client value).
      return ctx.fallback;
  }
}

export const createOrder = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => {
    const parsed = createOrderSchema.safeParse(input);
    if (!parsed.success) {
      const notesIssue = parsed.error.issues.find((issue) => issue.path[0] === "notes");
      throw new Error(notesIssue?.message ?? "Έλεγξε τα στοιχεία της παραγγελίας: κάποιο πεδίο λείπει ή ξεπερνά το επιτρεπόμενο όριο.");
    }
    return parsed.data;
  })
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { getOptionalUserId } = await import("./auth-optional.server");
    // SECURITY: never trust a client-supplied user id. Derive the real user
    // from the verified session token (attached automatically for logged-in
    // users); guests resolve to null and get a guest order.
    const authUserId = await getOptionalUserId();
    // SECURITY: never trust client-supplied prices. Re-fetch the authoritative
    // effective price (incl. VAT and active offers) for every item from the DB
    // and use those to compute the amount charged. This prevents tampering with
    // the cart payload to pay near-zero.
    const productIds = Array.from(new Set(data.items.map((it) => it.productId)));
    const { getAuthoritativePricing } = await import("./products.server");
    const pricingByProduct = await getAuthoritativePricing(productIds);
    const pricedItems = data.items.map((it) => {
      const info = pricingByProduct.get(it.productId);
      if (info == null) {
        throw new Error(`Το προϊόν δεν είναι διαθέσιμο: ${it.name}`);
      }
      return { ...it, price: info.price, onSale: info.onSale, taxRate: info.taxRate };
    });
    const computed = computeTotals(pricedItems);
    const subtotal = computed.subtotal;
    // Coupons never apply to products already on offer. The eligible base for any
    // coupon discount is the subtotal of the NON-discounted items only.
    const couponEligibleSubtotal =
      Math.round(
        pricedItems.reduce(
          (s, it) => s + (it.onSale ? 0 : it.price * it.quantity),
          0,
        ) * 100,
      ) / 100;
    // SECURITY: recompute the shipping cost server-side from the selected method
    // and the authoritative cart. The client value is never trusted (an attacker
    // could otherwise send shippingCost: 0). Falls back to the flat estimate for
    // unknown methods or when a courier quote is unavailable.
    const shipping = await recomputeShipping(data.shippingMethod, {
      items: data.items.map((it) => ({ productId: it.productId, quantity: it.quantity })),
      subtotal,
      address: {
        line1: data.shippingAddress.line1,
        city: data.shippingAddress.city,
        postalCode: data.shippingAddress.postalCode,
        country: data.shippingAddress.country,
      },
      fallback: computed.shipping,
    });
    // SECURITY: εύθραυστα προϊόντα δεν αποστέλλονται με courier. Ανεξάρτητα από
    // το τι στέλνει ο client, επιτρέπονται μόνο παραλαβή από το κατάστημα ή
    // παράδοση MrPanda.
    {
      const { getFragileSets } = await import("./fragile-products.functions");
      const { ids: fragileIds, eans: fragileEans } = await getFragileSets();
      if (fragileIds.size > 0 || fragileEans.size > 0) {
        const { data: prodRows } = await supabaseAdmin
          .from("products")
          .select("id, ps_id, reference")
          .in("id", productIds);
        const hasFragile = (prodRows ?? []).some(
          (r: { ps_id: number | null; reference: string | null }) =>
            (r.ps_id != null && fragileIds.has(r.ps_id)) ||
            (!!r.reference && fragileEans.has(r.reference)),
        );
        const allowedFragile = ["store_pickup", "mrpanda_delivery"];
        if (hasFragile && (!data.shippingMethod || !allowedFragile.includes(data.shippingMethod))) {
          throw new Error(
            "Το καλάθι περιέχει εύθραυστο προϊόν που αποστέλλεται μόνο με παραλαβή από το κατάστημα ή παράδοση MrPanda εντός Θεσσαλονίκης.",
          );
        }
      }
    }
    // SECURITY: re-validate the coupon server-side against the ops coupon API and
    // recompute the authoritative discount from the real subtotal. The client
    // never dictates the discount amount.
    let discount = 0;
    let appliedCoupon: string | null = null;
    if (data.couponCode && data.couponCode.trim() && couponEligibleSubtotal > 0) {
      const { validateCouponRemote } = await import("./coupons.server");
      const v = await validateCouponRemote(
        data.couponCode.trim(),
        couponEligibleSubtotal,
        data.email,
      );
      if (v.valid) {
        discount = Math.min(
          Math.round(v.discountAmount * 100) / 100,
          couponEligibleSubtotal,
        );
        appliedCoupon = v.code;
      }
    }
    // SECURITY: recompute the MrBox bundle discount server-side from the
    // authoritative cart and the rebuilt bundles. The client only declares which
    // bundles it added; the discount amount is never trusted from the client.
    // Προτιμάμε τις γραμμές του καλαθιού που φέρουν bundleId: τα πακέτα
    // ξαναχτίζονται δυναμικά, οπότε ένα rebuild μπορεί να μη περιέχει πια τα
    // ίδια προϊόντα και η έκπτωση να «χανόταν» (ο πελάτης έβλεπε άλλο ποσό στο
    // ταμείο και άλλο στη Viva). Το ποσοστό παραμένει server-side.
    {
      const bundleLines = pricedItems.filter((it) => !!it.bundleId);
      let bundleDiscount = 0;
      if (bundleLines.length > 0) {
        const { BUNDLE_DEFS, discountForSubtotal } = await import("./mrbox");
        const validIds = new Set(BUNDLE_DEFS.map((d) => d.id));
        const byBundle = new Map<string, number>();
        for (const it of bundleLines) {
          const bid = it.bundleId as string;
          if (!validIds.has(bid)) continue;
          // Κάθε αντίτυπο πακέτου (bundleKey) μετράει ξεχωριστά, ώστε δύο ίδια
          // πακέτα να μην ανεβάζουν τεχνητά το ποσοστό έκπτωσης.
          const key = it.bundleKey ?? bid;
          byBundle.set(key, (byBundle.get(key) ?? 0) + it.price * it.quantity);
        }
        for (const boxSubtotal of byBundle.values()) {
          bundleDiscount += boxSubtotal * discountForSubtotal(boxSubtotal);
        }
        bundleDiscount = Math.round(bundleDiscount * 100) / 100;
      } else if (data.bundleIds && data.bundleIds.length > 0) {
        const { recomputeBundleDiscount } = await import("./mrbox.server");
        bundleDiscount = await recomputeBundleDiscount(
          data.items.map((it) => ({ productId: it.productId, quantity: it.quantity })),
          data.bundleIds,
        );
      }
      if (bundleDiscount > 0) discount = Math.round((discount + bundleDiscount) * 100) / 100;
    }
    // SECURITY: recompute the island reduced-VAT discount server-side from the
    // authoritative per-item VAT rates. Applies only to invoices where BOTH the
    // delivery postal code AND the (AADE seat) postal code fall in a reduced-VAT
    // island zone. The client never dictates the amount.
    let islandVatDiscount = 0;
    let islandVatName: string | null = null;
    let islandVatEligible = false;
    if (data.documentType === "invoice" && data.shippingAddress.vatNumber.trim()) {
      const { computeIslandVatDiscount } = await import("./island-vat");
      const q = computeIslandVatDiscount(
        pricedItems.map((it) => ({
          price: it.price,
          quantity: it.quantity,
          taxRate: it.taxRate,
        })),
        data.shippingAddress.postalCode,
        data.shippingAddress.vatPostalCode,
      );
      if (q.eligible && q.vatDiscount > 0) {
        islandVatDiscount = q.vatDiscount;
        islandVatName = q.island;
        islandVatEligible = true;
        discount = Math.round((discount + q.vatDiscount) * 100) / 100;
      }
    }
    discount = Math.min(discount, subtotal);
    const total = Math.round((subtotal + shipping - discount) * 100) / 100;
    const guestToken = crypto.randomUUID();

    const externalId = `mp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const initialStatus = "pending";
    const initialPaymentStatus = "pending";

    const isOnlinePayment =
      data.paymentMethod === "viva" ||
      data.paymentMethod === "viva_card" ||
      data.paymentMethod === "klarna" ||
      data.paymentMethod === "iris";

    // Check the indexed top-level `external_code` column in the shared orders
    // table (it holds the same order number). Filtering through
    // `raw->>order_number` forced a full JSON scan of the large orders table and
    // could hit PostgreSQL's statement timeout during Viva checkout. Staged
    // orders still keep the number in their order_record JSON; the local
    // database has a matching expression index for that lookup.
    const orderNumber = await uniqueOrderNumber(async (candidate) => {
      const { data: finalOrders, error: finalError } = await supabaseAdmin
        .from("orders")
        .select("id")
        .eq("external_code", candidate)
        .limit(1);
      if (finalError) throw new Error(finalError.message);
      if ((finalOrders?.length ?? 0) > 0) return true;
      if (!isOnlinePayment) return false;
      const { localAdmin } = await import("./pending-orders.server");
      const { data: stagedOrders, error: stagedError } = await localAdmin
        .from("pending_online_orders")
        .select("id")
        .eq("order_record->raw->>order_number", candidate)
        .limit(1);
      if (stagedError) throw new Error(stagedError.message);
      return (stagedOrders?.length ?? 0) > 0;
    });

    // Enrich products with reference (sku) / ean13 (barcode) for both the
    // persisted order_items (picking) and the ops payload.
    const refByProduct = new Map<
      string,
      {
        sku?: string;
        barcode?: string;
        taxRate?: number | null;
        imageUrl?: string | null;
      }
    >();
    if (productIds.length > 0) {
      const { data: products } = await supabaseAdmin
        .from("products")
        .select("id, reference, ean13, upc, tax_rate, image_url")
        .in("id", productIds);
      for (const p of products ?? []) {
        const row = p as {
          id: string;
          reference: string | null;
          ean13: string | null;
          upc: string | null;
          tax_rate: number | null;
          image_url: string | null;
        };
        refByProduct.set(row.id, {
          sku: row.reference ?? undefined,
          barcode: row.ean13 ?? row.upc ?? undefined,
          taxRate: row.tax_rate,
          imageUrl: row.image_url,
        });
      }
    }

    // order_items rows WITHOUT order_id (added once the order row exists, either
    // immediately for offline orders or at promotion time for online ones).
    const itemsBase = pricedItems.map((it) => {
      const ref = refByProduct.get(it.productId);
      return {
        name: it.name,
        sku: ref?.sku ?? null,
        barcode: ref?.barcode ?? null,
        quantity: it.quantity,
        image_url: it.imageUrl ?? null,
      };
    });

    // Map our internal payment methods to the values the ops app expects.
    const opsPaymentMethod =
      data.paymentMethod === "viva" || data.paymentMethod === "viva_card"
        ? "card"
        : data.paymentMethod === "cash"
          ? "cod"
          : data.paymentMethod;

    // Map the country label to an ISO-2 code where we recognise it.
    const countryRaw = (data.shippingAddress.country ?? "").trim();
    const opsCountry = /ελλ|greece|greek/i.test(countryRaw)
      ? "GR"
      : /κύπρ|cyprus/i.test(countryRaw)
        ? "CY"
        : countryRaw || "GR";

    const isInvoice = !!data.shippingAddress.vatNumber?.trim();
    const nameParts = data.fullName.trim().split(/\s+/);
    const firstName = nameParts.shift() ?? "";
    const lastName = nameParts.join(" ");
    const round2 = (n: number) => Math.round(n * 100) / 100;

    // Build the ops/picking payload from the cart following the ops contract.
    const opsPayload = {
      external_id: externalId,
      external_code: orderNumber,
      customer_name: data.fullName,
      customer_email: data.email,
      customer_phone: data.phone,
      total,
      subtotal,
      shipping_cost: shipping,
      discount: discount || undefined,
      coupon_code: appliedCoupon ?? undefined,
      island_vat: islandVatEligible || undefined,
      island_vat_reduction: islandVatDiscount || undefined,
      island_vat_name: islandVatName ?? undefined,
      shipping_method: data.shippingMethod,
      shipping_method_label: data.shippingMethodLabel,
      payment_method: opsPaymentMethod,
      document_type: data.documentType,
      out_of_stock_action: data.outOfStockAction,
      notes: data.notes || undefined,
      // Structured locker info so ops can read the locker id without parsing the
      // free-text note. Present only for BoxNow / Skroutz Last Mile orders.
      pickup_point: data.pickupPoint
        ? {
            provider: data.pickupPoint.provider,
            id: data.pickupPoint.id,
            name: data.pickupPoint.name || undefined,
            address: data.pickupPoint.address || undefined,
            postcode: data.pickupPoint.postcode || undefined,
          }
        : undefined,
      // Offline orders start unpaid; the Viva webhook overrides this to "paid"
      // before forwarding the stashed payload once payment is confirmed.
      payment_status: initialPaymentStatus,
      shipping_address: {
        first_name: firstName,
        last_name: lastName,
        address1: data.shippingAddress.line1,
        address2: data.shippingAddress.line2 ?? "",
        city: data.shippingAddress.city,
        postcode: data.shippingAddress.postalCode,
        country: opsCountry,
        phone: data.phone,
        is_invoice: isInvoice,
        company: data.invoice?.company ?? "",
        vat: data.shippingAddress.vatNumber ?? "",
        doy: data.invoice?.doy ?? "",
      },
      items: pricedItems.map((it) => {
        const ref = refByProduct.get(it.productId);
        const taxRate = ref?.taxRate;
        const unitPriceExcl =
          taxRate != null && taxRate > 0
            ? round2(it.price / (1 + taxRate / 100))
            : undefined;
        // Μειωμένος ΦΠΑ νησιών: όταν η παραγγελία είναι επιλέξιμη, στέλνουμε
        // ΚΑΙ τον μειωμένο συντελεστή + τη μειωμένη μικτή/καθαρή τιμή ανά γραμμή,
        // ώστε το ops να εκδίδει το τιμολόγιο απευθείας με 17/9/4 (όχι 24/13/6).
        const reducedRate = islandVatEligible ? reducedVatRate(taxRate) : null;
        const hasReduced =
          reducedRate != null && taxRate != null && reducedRate < taxRate;
        // Καθαρή τιμή (χωρίς ΦΠΑ) — μένει ίδια· αλλάζει μόνο ο συντελεστής ΦΠΑ.
        const netUnit =
          taxRate != null && taxRate > 0
            ? round2(it.price / (1 + taxRate / 100))
            : it.price;
        const reducedUnitPrice = hasReduced
          ? round2(netUnit * (1 + reducedRate! / 100))
          : undefined;
        return {
          product_id: it.productId,
          name: it.name,
          quantity: it.quantity,
          sku: ref?.sku,
          barcode: ref?.barcode,
          image_url: it.imageUrl ?? ref?.imageUrl ?? undefined,
          unit_price: it.price,
          unit_price_excl: unitPriceExcl,
          total_price: round2(it.price * it.quantity),
          // ΦΠΑ: κανονικός συντελεστής της γραμμής (π.χ. 24) και, αν εφαρμόζεται
          // μειωμένος ΦΠΑ νησιών, ο μειωμένος συντελεστής (π.χ. 17) + οι τιμές.
          tax_rate: taxRate ?? undefined,
          reduced_vat: hasReduced || undefined,
          reduced_tax_rate: hasReduced ? reducedRate! : undefined,
          reduced_unit_price: reducedUnitPrice,
          reduced_total_price: hasReduced
            ? round2(reducedUnitPrice! * it.quantity)
            : undefined,
        };
      }),
    };

    // The shared `orders` insert object (customer_id resolved per-branch).
    const baseOrderRecord = {
      source: "eshop",
      external_id: externalId,
      external_code: orderNumber,
      status: initialStatus,
      payment_status: initialPaymentStatus,
      // The shared `orders` table's check constraint accepts "cod" (not "cash")
      // for cash-on-delivery — mirror the store-pickup flow so the insert passes.
      payment_method: data.paymentMethod === "cash" ? "cod" : data.paymentMethod,
      customer_email: data.email,
      customer_phone: data.phone,
      customer_name: data.fullName,
      shipping_address: { ...data.shippingAddress, notes: data.notes },
      subtotal,
      shipping_cost: shipping,
      total,
      guest_access_token: guestToken,
      raw: {
        order_number: orderNumber,
        shipping_method: opsPayload.shipping_method,
        shipping_method_label: opsPayload.shipping_method_label,
        carrier_name: resolveCarrierName(data.shippingMethod) ?? data.shippingMethod,
        pickup_point: opsPayload.pickup_point,
        coupon_code: appliedCoupon ?? undefined,
        discount: discount || undefined,
        island_vat_reduction: islandVatDiscount || undefined,
        island_vat_name: islandVatName ?? undefined,
        items: opsPayload.items,
        eshop_ops_payload: opsPayload,
      },
    };

    // Direct Klarna never creates a Viva checkout or exposes unpaid orders to ops.
    if (paymentProvider(data.paymentMethod) === "klarna") {
      const { localAdmin } = await import("./pending-orders.server");
      const { createKlarnaSession } = await import("./klarna.server");
      const { buildKlarnaOrderLines } = await import("./klarna-pricing");
      const built = buildKlarnaOrderLines(opsPayload.items, shipping, total);
      const session = await createKlarnaSession({
        order_amount: built.orderAmount, order_tax_amount: built.orderTaxAmount,
        order_lines: built.orderLines, merchant_reference1: orderNumber,
        customer_email: data.email,
      });
      if (!session.categories.length) throw new Error("Η Klarna δεν προσφέρει διαθέσιμο τρόπο πληρωμής για αυτή την παραγγελία.");
      const { error } = await localAdmin.from("pending_online_orders").insert({
        guest_access_token: guestToken, payment_status: "pending",
        klarna_session_id: session.sessionId,
        order_record: baseOrderRecord, items: itemsBase, ops_payload: opsPayload,
        customer: authUserId ? { auth_user_id: authUserId, email: data.email,
          full_name: data.fullName, phone: data.phone } : null,
      });
      if (error) throw new Error("Δεν αποθηκεύτηκε η παραγγελία. Δεν έγινε χρέωση· δοκίμασε ξανά.");
      return { orderId: null as string | null, guestToken, redirectUrl: null,
        klarna: { clientToken: session.clientToken, categories: session.categories } };
    }

    // ---- Online (Viva) payments ----
    // These MUST NOT touch the shared orders table (OSP reads it directly) until
    // payment is confirmed. We create the Viva checkout, then stage the whole
    // order in THIS project's private `pending_online_orders` table. The Viva
    // webhook promotes it into the shared orders table once paid.
    if (paymentProvider(data.paymentMethod) === "viva") {
      const { createVivaOrder, CHECKOUT_BASE } = await import("./viva.server");
      const { localAdmin } = await import("./pending-orders.server");
      // IRIS (like the plain "viva" option) opens the full Viva Smart Checkout —
      // no paymentMethodIds restriction — so the customer can pick IRIS (or any
      // method) inside Viva's own page. "viva_card" pins the card channel.
      const preferred = data.paymentMethod === "viva_card" ? "card" as const : undefined;
      // Show the customer-facing order number on the Viva checkout.
      const displayCode = orderNumber;
      const { orderCode } = await createVivaOrder({
        amountCents: Math.round(total * 100),
        customerEmail: data.email,
        customerFullName: data.fullName,
        customerPhone: data.phone,
        merchantTrns: guestToken,
        customerTrns: `MrPanda.gr παραγγελία ${displayCode}`,
        preferredPaymentMethod: preferred,
      });

      const { error: pendingErr } = await localAdmin.from("pending_online_orders").insert({
        guest_access_token: guestToken,
        viva_order_code: orderCode,
        payment_status: "pending",
        // All Viva-processed methods (viva, viva_card, IRIS) settle through the
        // Viva Smart Checkout, so the SHARED orders table — whose
        // `orders_payment_method_check` constraint does NOT accept "iris" or
        // "viva_card" — must record them as "viva". Otherwise promotion fails
        // the constraint and the paid order stays stuck "pending" forever. The
        // ops payload still carries the precise method ("card") separately.
        order_record: { ...baseOrderRecord, payment_method: "viva", viva_order_code: orderCode },
        items: itemsBase,
        ops_payload: opsPayload,
        customer: authUserId
          ? {
              auth_user_id: authUserId,
              email: data.email,
              full_name: data.fullName,
              phone: data.phone,
            }
          : null,
      });
      if (pendingErr) throw new Error(pendingErr.message);

      return {
        orderId: null as string | null,
        guestToken,
        redirectUrl: `${CHECKOUT_BASE}?ref=${orderCode}`,
      };
    }

    // ---- Offline payments (cash / bank transfer): final immediately ----
    // Upsert customer if logged in.
    let customerId: string | null = null;
    if (authUserId) {
      const { data: existing } = await supabaseAdmin
        .from("customers")
        .select("id")
        .eq("auth_user_id", authUserId)
        .maybeSingle();
      if (existing) {
        customerId = existing.id as string;
      } else {
        const { data: ins } = await supabaseAdmin
          .from("customers")
          .insert({
            auth_user_id: authUserId,
            email: data.email,
            full_name: data.fullName,
            phone: data.phone,
          })
          .select("id")
          .single();
        customerId = (ins?.id as string | undefined) ?? null;
      }
    }

    // IDEMPOTENCY GUARD (prevents accidental duplicate orders).
    // If the customer double-submits — e.g. the first attempt errored after the
    // order row was written and they retried — we must not create a second
    // order. Look for an identical eshop order (same phone + same cart) placed
    // in the last few minutes and, if found, return it instead of inserting a
    // duplicate. The cart "signature" is the sorted list of productId:quantity.
    const orderSignature = pricedItems
      .map((it) => `${it.productId}:${it.quantity}`)
      .sort()
      .join("|");
    const dedupSince = new Date(Date.now() - 5 * 60 * 1000).toISOString();
    const { data: recentOrders } = await supabaseAdmin
      .from("orders")
      .select("id, guest_access_token, raw, created_at")
      .eq("source", "eshop")
      .eq("customer_phone", data.phone)
      .gte("created_at", dedupSince)
      .order("created_at", { ascending: false })
      .limit(5);
    const recentRows = (recentOrders ?? []) as unknown as Array<{
      id: string;
      guest_access_token: string | null;
      raw?: {
        eshop_ops_payload?: { items?: Array<{ product_id?: string; quantity?: number }> };
      } | null;
    }>;
    for (const r of recentRows) {
      const existingItems = r.raw?.eshop_ops_payload?.items ?? [];
      const existingSignature = existingItems
        .map((i) => `${i.product_id}:${i.quantity}`)
        .sort()
        .join("|");
      if (existingSignature && existingSignature === orderSignature) {
        return {
          orderId: r.id,
          guestToken: r.guest_access_token ?? guestToken,
          redirectUrl: null,
        };
      }
    }

    const { data: inserted, error: insertError } = await supabaseAdmin
      .from("orders")
      .insert({ ...baseOrderRecord, customer_id: customerId })
      .select("id, guest_access_token")
      .single();
    if (insertError || !inserted) {
      throw new Error(insertError?.message ?? "Order insert failed");
    }
    const order = inserted as { id: string; guest_access_token: string | null };
    const orderId = order.id as string;

    // ATOMICITY: order + items must succeed together. If the items insert fails
    // we delete the just-created order row, otherwise a phantom order with no
    // items is left in the shared table (which ops reads directly) and the
    // customer, seeing an error, retries and ends up with a duplicate order.
    const itemsRows = itemsBase.map((it) => ({ order_id: orderId, ...it }));
    const { error: itemsErr } = await supabaseAdmin.from("order_items").insert(itemsRows);
    if (itemsErr) {
      await supabaseAdmin.from("orders").delete().eq("id", orderId);
      throw new Error(itemsErr.message);
    }

    // Offline orders are final the moment they are placed → notify ops now.
    const { notifyOpsOrderCreated } = await import("./notify-ops.functions");
    notifyOpsOrderCreated(opsPayload).catch((e) =>
      console.error("[createOrder] notifyOps failed:", e),
    );

    // Redeem the coupon now that the order is final (idempotent on externalId).
    if (appliedCoupon) {
      const { redeemCouponRemote } = await import("./coupons.server");
      redeemCouponRemote({
        code: appliedCoupon,
        cartTotal: couponEligibleSubtotal,
        orderRef: externalId,
        customerEmail: data.email,
      }).catch((e) => console.error("[createOrder] coupon redeem failed:", e));
    }

    return {
      orderId,
      guestToken: order.guest_access_token as string,
      redirectUrl: null,
    };
  });

// ---------------------------------------------------------------------------
// Store pickup orders (Παραλαβή από το κατάστημα)
// ---------------------------------------------------------------------------
// A lightweight order flow placed directly from the product page. The customer
// picks up the item at the physical shop and pays there (cash), so there is no
// shipping cost and no online payment. Logged-in customers only choose the
// quantity; guests also provide their name and a Greek mobile number.

// Physical store address used as the order's shipping address for pickups.
const STORE_PICKUP_ADDRESS = {
  line1: "Π. Λεβαντή 15Α",
  line2: "",
  city: "Σταυρούπολη Θεσσαλονίκης",
  region: "Θεσσαλονίκη",
  postalCode: "56429",
  country: "Ελλάδα",
  vatNumber: "",
};
const STORE_PICKUP_LABEL = "Παραλαβή από το κατάστημα";
// Repeated in the order notes so it also reaches the customer's order email.
const STORE_PICKUP_NOTES = [
  STORE_PICKUP_LABEL,
  "Θα λάβεις SMS μόλις η παραγγελία σου είναι έτοιμη. Παρακαλούμε μην προσέλθεις στο κατάστημα πριν την ειδοποίηση.",
].join("\n");

const storePickupSchema = z.object({
  productId: z.string().min(1).max(80),
  quantity: z.number().int().min(1).max(999),
  // Guest-only fields (ignored when the caller is authenticated).
  guestName: z.string().trim().min(2).max(120).optional(),
  guestPhone: z
    .string()
    .trim()
    .refine((v) => isMobilePhone(v), "Δώσε έγκυρο κινητό Ελλάδας (69…) ή Κύπρου (9XXXXXXX)")
    .optional(),
  guestEmail: z.string().trim().email("Μη έγκυρο email").max(255).optional(),
});

export const createStorePickupOrder = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => storePickupSchema.parse(input))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { getOptionalUserId } = await import("./auth-optional.server");
    const { getAuthoritativePrices } = await import("./products.server");

    // SECURITY: derive the user from the verified session token; never trust the
    // client. Guests resolve to null and must supply name + phone.
    const authUserId = await getOptionalUserId();

    // Resolve the customer identity.
    let fullName = "";
    let email = "";
    let phone = "";
    if (authUserId) {
      const { data: existing } = await supabaseAdmin
        .from("customers")
        .select("full_name, email, phone")
        .eq("auth_user_id", authUserId)
        .maybeSingle();
      const cust = existing as
        | { full_name: string | null; email: string | null; phone: string | null }
        | null;
      fullName = cust?.full_name?.trim() || "";
      email = cust?.email?.trim() || "";
      phone = cust?.phone?.trim() || "";
      if (!fullName || !email) {
        const { data: authData } = await supabaseAdmin.auth.admin.getUserById(authUserId);
        const u = authData?.user;
        if (u) {
          if (!email) email = u.email ?? "";
          if (!fullName) {
            const meta = (u.user_metadata ?? {}) as Record<string, unknown>;
            const metaName =
              (typeof meta.full_name === "string" && meta.full_name.trim()) ||
              (typeof meta.name === "string" && meta.name.trim()) ||
              [meta.first_name ?? meta.firstname, meta.last_name ?? meta.lastname]
                .filter((p): p is string => typeof p === "string" && p.trim().length > 0)
                .join(" ")
                .trim() ||
              "";
            fullName = metaName.trim() || (u.email ? u.email.split("@")[0] : "Πελάτης");
          }
        }
      }
      if (!email) email = `pickup+${authUserId.slice(0, 8)}@mrpanda.gr`;
      // Imported / social accounts can have no phone on the customer record.
      // Accept the one typed in the dialog, otherwise fall back to the auth
      // metadata; a pickup order without a phone cannot be notified by SMS, so
      // it is rejected instead of being stored with an empty phone.
      if (!isMobilePhone(phone)) {
        phone = data.guestPhone?.trim() || "";
      }
      if (!isMobilePhone(phone)) {
        const { data: authData } = await supabaseAdmin.auth.admin.getUserById(authUserId);
        const meta = (authData?.user?.user_metadata ?? {}) as Record<string, unknown>;
        const metaPhone =
          typeof meta.phone === "string" ? normalizePhoneDigits(meta.phone) : "";
        if (isMobilePhone(metaPhone)) phone = metaPhone;
      }
      if (!isMobilePhone(phone)) {
        throw new Error("Χρειαζόμαστε κινητό τηλέφωνο για την ειδοποίηση παραλαβής.");
      }
      // Keep the profile up to date so the next order does not ask again.
      await supabaseAdmin
        .from("customers")
        .update({ phone })
        .eq("auth_user_id", authUserId);
    } else {
      if (!data.guestName || !data.guestPhone || !data.guestEmail) {
        throw new Error("Χρειαζόμαστε ονοματεπώνυμο, τηλέφωνο και email για την παραλαβή.");
      }
      fullName = data.guestName;
      phone = data.guestPhone;
      email = data.guestEmail;
    }

    // SECURITY: re-fetch the authoritative price + product details from the DB.
    const priceByProduct = await getAuthoritativePrices([data.productId]);
    const unitPrice = priceByProduct.get(data.productId);
    if (unitPrice == null) {
      throw new Error("Το προϊόν δεν είναι διαθέσιμο.");
    }
    const { data: prod } = await supabaseAdmin
      .from("products")
      .select("id, name, reference, ean13, upc, tax_rate, image_url, stock_quantity")
      .eq("id", data.productId)
      .maybeSingle();
    const product = prod as
      | {
          id: string;
          name: string | null;
          reference: string | null;
          ean13: string | null;
          upc: string | null;
          tax_rate: number | null;
          image_url: string | null;
          stock_quantity: number | null;
        }
      | null;
    if (!product) throw new Error("Το προϊόν δεν βρέθηκε.");
    if (product.stock_quantity != null && product.stock_quantity <= 0) {
      throw new Error("Το προϊόν είναι εξαντλημένο.");
    }

    const productName = product.name ?? "Προϊόν";
    const sku = product.reference ?? undefined;
    const barcode = product.ean13 ?? product.upc ?? undefined;
    const taxRate = product.tax_rate;

    const subtotal = round2(unitPrice * data.quantity);
    const shipping = 0;
    const total = subtotal;
    const guestToken = crypto.randomUUID();
    const externalId = `mp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const orderNumber = await uniqueOrderNumber(async (candidate) => {
      const { data: existingOrders, error } = await supabaseAdmin
        .from("orders")
        .select("id")
        .eq("external_code", candidate)
        .limit(1);
      if (error) throw new Error(error.message);
      return (existingOrders?.length ?? 0) > 0;
    });

    const nameParts = fullName.trim().split(/\s+/);
    const firstName = nameParts.shift() ?? "";
    const lastName = nameParts.join(" ");

    const unitPriceExcl =
      taxRate != null && taxRate > 0 ? round2(unitPrice / (1 + taxRate / 100)) : undefined;

    const opsPayload = {
      external_id: externalId,
      external_code: orderNumber,
      customer_name: fullName,
      customer_email: email,
      customer_phone: phone,
      total,
      subtotal,
      shipping_cost: shipping,
      shipping_method: "store_pickup",
      shipping_method_label: STORE_PICKUP_LABEL,
      payment_method: "cod",
      payment_status: "pending",
      notes: STORE_PICKUP_NOTES,
      shipping_address: {
        first_name: firstName,
        last_name: lastName,
        address1: STORE_PICKUP_ADDRESS.line1,
        address2: STORE_PICKUP_ADDRESS.line2,
        city: STORE_PICKUP_ADDRESS.city,
        postcode: STORE_PICKUP_ADDRESS.postalCode,
        country: "GR",
        phone,
        is_invoice: false,
        company: "",
        vat: "",
        doy: "",
      },
      items: [
        {
          product_id: data.productId,
          name: productName,
          quantity: data.quantity,
          sku,
          barcode,
          image_url: product.image_url ?? undefined,
          unit_price: unitPrice,
          unit_price_excl: unitPriceExcl,
          total_price: subtotal,
        },
      ],
    };

    const baseOrderRecord = {
      source: "eshop",
      external_id: externalId,
      status: "pending",
      payment_status: "pending",
      payment_method: "cod",
      customer_email: email,
      customer_phone: phone,
      customer_name: fullName,
      shipping_address: {
        ...STORE_PICKUP_ADDRESS,
        notes: STORE_PICKUP_NOTES,
        shipping_method: "store_pickup",
        shipping_method_label: STORE_PICKUP_LABEL,
      },
      subtotal,
      shipping_cost: shipping,
      total,
      guest_access_token: guestToken,
      raw: { order_number: orderNumber, store_pickup: true },
    };

    // Upsert the customer record when logged in.
    let customerId: string | null = null;
    if (authUserId) {
      const { data: existing } = await supabaseAdmin
        .from("customers")
        .select("id")
        .eq("auth_user_id", authUserId)
        .maybeSingle();
      if (existing) {
        customerId = existing.id as string;
      } else {
        const { data: ins } = await supabaseAdmin
          .from("customers")
          .insert({ auth_user_id: authUserId, email, full_name: fullName, phone })
          .select("id")
          .single();
        customerId = (ins?.id as string | undefined) ?? null;
      }
    }

    const { data: inserted, error: insertError } = await supabaseAdmin
      .from("orders")
      .insert({ ...baseOrderRecord, customer_id: customerId })
      .select("id, guest_access_token")
      .single();
    if (insertError || !inserted) {
      throw new Error(insertError?.message ?? "Order insert failed");
    }
    const order = inserted as { id: string; guest_access_token: string | null };
    const orderId = order.id as string;

    const { error: itemsErr } = await supabaseAdmin.from("order_items").insert([
      {
        order_id: orderId,
        name: productName,
        sku: sku ?? null,
        barcode: barcode ?? null,
        quantity: data.quantity,
        image_url: product.image_url ?? null,
      },
    ]);
    if (itemsErr) throw new Error(itemsErr.message);

    // Store-pickup orders are final immediately → notify ops now.
    const { notifyOpsOrderCreated } = await import("./notify-ops.functions");
    notifyOpsOrderCreated(opsPayload).catch((e) =>
      console.error("[createStorePickupOrder] notifyOps failed:", e),
    );

    return {
      orderId,
      guestToken: order.guest_access_token as string,
      orderNumber,
    };
  });

// Surface fulfillment fields the ops/courier flow stores inside the order's raw
// JSON: a tracking number, the courier voucher URL (its presence means the
// parcel has been packed/labelled), the carrier name, and a delivered_at stamp.
function extractFulfillment(raw: Record<string, unknown> | null): {
  tracking_number: string | null;
  voucher_url: string | null;
  carrier_name: string | null;
  delivered_at: string | null;
} {
  const r = raw ?? {};
  const str = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : null);
  const codes = Array.isArray(r.courier_tracking_codes) ? r.courier_tracking_codes : [];
  const firstCode = str(codes[0]);
  const tracking = str(r.tracking_number) ?? str(r.shipping_number) ?? firstCode;
  return {
    tracking_number: tracking,
    voucher_url: str(r.courier_voucher),
    carrier_name: str(r.carrier_name) ?? str(r.courier),
    delivered_at: str(r.delivered_at),
  };
}

export const getOrderByToken = createServerFn({ method: "GET" })
  .inputValidator((input: { token: string }) => z.object({ token: z.string().uuid() }).parse(input))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { localAdmin } = await import("./pending-orders.server");
    const { CHECKOUT_BASE } = await import("./viva.server");
    const { data: order } = await supabaseAdmin
      .from("orders")
      .select(
        "id, source, external_id, external_code, status, payment_status, payment_method, customer_email, customer_name, customer_phone, shipping_address, subtotal, shipping_cost, total, created_at, viva_order_code, guest_access_token, raw",
      )
      .eq("guest_access_token", data.token)
      .maybeSingle();
    const orderRow = order as unknown as
      | (Record<string, unknown> & {
          id: string;
          raw?:
            | (Record<string, unknown> & {
                order_number?: string | null;
                reference?: string | null;
              })
            | null;
        })
      | null;
    if (!orderRow) {
      // Not in the shared table → it may be a staged online order awaiting
      // payment (lives in this project's private DB). Surface a minimal,
      // payment-pending view so the confirmation page can offer "continue".
      const { data: pending } = await localAdmin
        .from("pending_online_orders")
        .select("payment_status, viva_order_code, order_record")
        .eq("guest_access_token", data.token)
        .maybeSingle();
      if (!pending) return { order: null as Json, items: [] as Json[] };
      const p = pending as {
        payment_status: string;
        viva_order_code: string | null;
        order_record: Record<string, unknown> & { raw?: { order_number?: string | null } | null };
      };
      const pendingOrder = {
        external_id: p.order_record.external_id ?? null,
        status: "pending",
        payment_status: p.payment_status,
        payment_method: p.order_record.payment_method ?? null,
        customer_email: p.order_record.customer_email ?? null,
        customer_name: p.order_record.customer_name ?? null,
        total: p.order_record.total ?? null,
        order_number: p.order_record.raw?.order_number ?? null,
        viva_checkout_url:
          p.payment_status !== "paid" && p.viva_order_code
            ? `${CHECKOUT_BASE}?ref=${p.viva_order_code}`
            : null,
      };
      return {
        order: JSON.parse(JSON.stringify(pendingOrder)) as Json,
        items: [] as Json[],
      };
    }
    const { data: items } = await supabaseAdmin
      .from("order_items")
      .select("id, name, quantity, image_url, sku, barcode")
      .eq("order_id", orderRow.id);
    // Imported orders store SKU/barcode but no image — backfill from the catalog.
    await backfillItemImages(supabaseAdmin, (items ?? []) as Array<Record<string, unknown>>);
    // The shared order_items table has no unit_price column — resolve current
    // catalog prices by SKU so the order page can show per-product prices.
    {
      const itemRows = (items ?? []) as Array<Record<string, unknown>>;
      const skus = new Set<string>();
      for (const it of itemRows) {
        const sku = typeof it.sku === "string" ? it.sku : "";
        if (sku) skus.add(sku);
      }
      if (skus.size > 0) {
        const { data: products } = await supabaseAdmin
          .from("products")
          .select("reference, price")
          .in("reference", Array.from(skus));
        const priceBySku = new Map<string, number>();
        for (const p of products ?? []) {
          const row = p as { reference: string | null; price: number | null };
          if (row.reference && row.price != null && Number.isFinite(row.price)) {
            priceBySku.set(row.reference, +row.price.toFixed(2));
          }
        }
        for (const it of itemRows) {
          const sku = typeof it.sku === "string" ? it.sku : "";
          it.unit_price = priceBySku.get(sku) ?? null;
        }
      }
    }
    // The order number lives inside raw (no dedicated column on the shared
    // orders table); surface it as order_number and drop the raw blob.
    const { raw, ...orderRest } = orderRow;
    // For online (Viva) orders still awaiting payment, build the checkout URL
    // so the confirmation page can offer a "continue payment" CTA.
    const vivaCode = (orderRest as { viva_order_code?: unknown }).viva_order_code;
    let payStatus = String((orderRest as { payment_status?: unknown }).payment_status ?? "");
    const payMethod = String((orderRest as { payment_method?: unknown }).payment_method ?? "");
    const isOnline =
      payMethod === "viva" ||
      payMethod === "viva_card" ||
      payMethod === "klarna" ||
      payMethod === "iris";
    // Online orders are stored "pending" in the shared table (a shared trigger
    // rejects payment_status="paid"); the real paid state lives in our private
    // staging table — read it back so the page reflects the actual payment.
    if (isOnline && payStatus !== "paid") {
      const { data: pend } = await localAdmin
        .from("pending_online_orders")
        .select("payment_status")
        .eq("guest_access_token", data.token)
        .maybeSingle();
      if ((pend as { payment_status?: string } | null)?.payment_status === "paid") {
        payStatus = "paid";
      }
    }
    const vivaCheckoutUrl =
      isOnline && payStatus !== "paid" && vivaCode
        ? `${CHECKOUT_BASE}?ref=${vivaCode}`
        : null;
    // Fill in totals / payment that imported orders leave empty (from raw).
    const derived = deriveLegacyOrderFields(
      {
        subtotal: orderRest.subtotal,
        shipping_cost: orderRest.shipping_cost,
        total: orderRest.total,
        payment_method: payMethod,
        payment_status: payStatus,
        status: orderRest.status,
      },
      raw ?? null,
    );
    const cleanOrder = {
      ...orderRest,
      subtotal: derived.subtotal,
      shipping_cost: derived.shipping_cost,
      payment_method: displayedPaymentMethod(derived.payment_method, raw),
      payment_status: derived.payment_status,
      order_number:
        (orderRest as { external_code?: string | null }).external_code ??
        raw?.order_number ??
        (typeof raw?.reference === "string" ? raw.reference : null),
      viva_checkout_url: vivaCheckoutUrl,
      ...extractFulfillment(raw ?? null),
    };
    return {
      order: JSON.parse(JSON.stringify(cleanOrder)) as Json,
      items: JSON.parse(JSON.stringify(items ?? [])) as Json[],
    };
  });

// Resolve a Viva orderCode (returned in the redirect as ?s=...) to the
// order's guest_access_token so we can send the customer to /order/{token}.
export const getTokenByVivaCode = createServerFn({ method: "GET" })
  .inputValidator((input: { code: string; transactionId?: string }) =>
    z
      .object({
        code: z.string().min(1).max(64).regex(/^\d+$/),
        transactionId: z.string().min(1).max(64).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: order } = await supabaseAdmin
      .from("orders")
      .select("guest_access_token")
      .eq("viva_order_code", data.code)
      .maybeSingle();
    const sharedToken = (order as { guest_access_token: string | null } | null)?.guest_access_token;
    if (sharedToken) return { token: sharedToken };
    // Staged online order (awaiting payment) lives in this project's private DB.
    // IRIS redirects (and the occasional card redirect) can land the shopper here
    // before Viva's webhook promoted the order — and a transiently lost webhook
    // can leave a genuinely paid order stuck "pending" forever. Reconcile directly
    // with Viva: confirm payment, promote if paid, and always return the token so
    // the shopper reaches their order page either way.
    const { reconcilePendingVivaByCode } = await import("./viva-webhook.server");
    const { token } = await reconcilePendingVivaByCode(data.code, data.transactionId ?? null);
    return { token };
  });


// Fallback resolver: given a Viva transaction id (returned in the redirect as
// ?t=...), look up the transaction and use its `merchantTrns` — which we set to
// the order's guest_access_token at creation time — to send the customer to
// /order/{token}. This path is immune to the 16-digit orderCode precision
// issue and works even when the ?s= (orderCode) lookup fails.
export const getTokenByVivaTransaction = createServerFn({ method: "GET" })
  .inputValidator((input: { transactionId: string }) =>
    z
      .object({ transactionId: z.string().min(1).max(64) })
      .parse(input),
  )
  .handler(async ({ data }) => {
    try {
      const { getVivaTransaction } = await import("./viva.server");
      const tx = await getVivaTransaction(data.transactionId);
      const token = (tx.merchantTrns ?? "").trim();
      // merchantTrns holds our guest_access_token (a UUID). Validate loosely.
      if (token && /^[0-9a-fA-F-]{20,}$/.test(token)) {
        return { token };
      }
      return { token: null as string | null };
    } catch {
      return { token: null as string | null };
    }
  });

export const listMyOrders = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { userId, claims } = context;

    // Email of the logged-in customer (from the customers table or the auth user)
    const { data: customer } = await supabaseAdmin
      .from("customers")
      .select("id, email")
      .eq("auth_user_id", userId)
      .maybeSingle();
    const email = ((customer?.email as string | undefined) ?? (claims?.email as string | undefined))
      ?.trim()
      .toLowerCase();

    // Gather every customer record that shares the same email, even if it has a
    // different id / auth_user_id, so all their orders show together.
    const customerIds = new Set<string>();
    if (customer?.id) customerIds.add(customer.id as string);
    if (email) {
      const { data: sameEmail } = await supabaseAdmin
        .from("customers")
        .select("id")
        .ilike("email", email);
      for (const c of sameEmail ?? []) customerIds.add(c.id as string);
    }

    // Build an OR filter that matches by any of those customer ids OR the email
    const cols =
      "id, external_id, status, payment_status, payment_method, total, created_at, guest_access_token";
    const orParts: string[] = [];
    if (customerIds.size > 0) {
      orParts.push(`customer_id.in.(${Array.from(customerIds).join(",")})`);
    }
    if (email) {
      orParts.push(`customer_email.ilike.${email}`);
      // Imported (PrestaShop) orders keep the email only inside raw.customer_email
      orParts.push(`raw->>customer_email.ilike.${email}`);
    }
    if (orParts.length === 0) return { orders: [] };

    const { data } = await supabaseAdmin
      .from("orders")
      .select(cols)
      .or(orParts.join(","))
      .order("created_at", { ascending: false })
      .limit(50);
    return { orders: data ?? [] };
  });

export type SavedAddress = {
  fullName: string;
  phone: string;
  line1: string;
  line2: string;
  city: string;
  postalCode: string;
};

export const listMyOrdersDetailed = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { userId, claims } = context;

    const { data: customer } = await supabaseAdmin
      .from("customers")
      .select("id, email")
      .eq("auth_user_id", userId)
      .maybeSingle();
    const email = ((customer?.email as string | undefined) ?? (claims?.email as string | undefined))
      ?.trim()
      .toLowerCase();

    const customerIds = new Set<string>();
    if (customer?.id) customerIds.add(customer.id as string);
    if (email) {
      const { data: sameEmail } = await supabaseAdmin
        .from("customers")
        .select("id")
        .ilike("email", email);
      for (const c of sameEmail ?? []) customerIds.add(c.id as string);
    }

    const orParts: string[] = [];
    if (customerIds.size > 0) {
      orParts.push(`customer_id.in.(${Array.from(customerIds).join(",")})`);
    }
    if (email) {
      orParts.push(`customer_email.ilike.${email}`);
      orParts.push(`raw->>customer_email.ilike.${email}`);
    }
    if (orParts.length === 0) return { orders: [] as Json[] };

    const { data: orders } = await supabaseAdmin
      .from("orders")
      .select(
        "id, source, external_id, external_code, status, payment_status, payment_method, subtotal, shipping_cost, total, created_at, guest_access_token, raw",
      )
      .or(orParts.join(","))
      .order("created_at", { ascending: false })
      .limit(50);

    const orderList = (orders ?? []) as unknown as Array<{
      id: string;
      source?: string | null;
      external_id: string | null;
      external_code?: string | null;
      status: string | null;
      payment_status: string | null;
      payment_method: string | null;
      subtotal: number | null;
      shipping_cost: number | null;
      total: number | null;
      created_at: string;
      guest_access_token: string | null;
      raw?:
        | (Record<string, unknown> & { order_number?: string | null; reference?: string | null })
        | null;
    }>;
    const ids = orderList.map((o) => o.id);
    const itemsByOrder: Record<string, unknown[]> = {};
    if (ids.length > 0) {
      const { data: items } = await supabaseAdmin
        .from("order_items")
        .select("id, order_id, name, quantity, image_url, sku, barcode")
        .in("order_id", ids);
      for (const it of items ?? []) {
        const oid = (it as { order_id: string }).order_id;
        (itemsByOrder[oid] ??= []).push(it);
      }
    }
    // Look up current product prices by SKU so the return dialog can enforce a
    // minimum return value. Falls back to null when the SKU is missing or the
    // product is no longer in the catalog.
    const allSkus = new Set<string>();
    for (const items of Object.values(itemsByOrder)) {
      for (const it of items) {
        const sku = typeof (it as { sku?: unknown }).sku === "string" ? (it as { sku: string }).sku : "";
        if (sku) allSkus.add(sku);
      }
    }
    const priceBySku = new Map<string, number>();
    if (allSkus.size > 0) {
      const { data: products } = await supabaseAdmin
        .from("products")
        .select("reference, price")
        .in("reference", Array.from(allSkus));
      for (const p of products ?? []) {
        const row = p as { reference: string | null; price: number | null };
        if (row.reference && row.price != null && Number.isFinite(row.price)) {
          priceBySku.set(row.reference, +row.price.toFixed(2));
        }
      }
    }
    for (const items of Object.values(itemsByOrder)) {
      for (const it of items) {
        const sku = typeof (it as { sku?: unknown }).sku === "string" ? (it as { sku: string }).sku : "";
        (it as Record<string, unknown>).unit_price = priceBySku.get(sku) ?? null;
      }
    }
    // Imported orders store SKU/barcode but no image — backfill from the catalog.
    await backfillItemImages(
      supabaseAdmin,
      Object.values(itemsByOrder).flat() as Array<Record<string, unknown>>,
    );

    // Online (Viva) orders never carry payment_status="paid" in the shared
    // table because a shared trigger rejects that value. The authoritative paid
    // state lives in this project's private staging table, so look it up and
    // override the displayed payment_status for promoted online orders.
    const paidOnlineIds = new Set<string>();
    if (ids.length > 0) {
      const { localAdmin } = await import("./pending-orders.server");
      const { data: promoted } = await localAdmin
        .from("pending_online_orders")
        .select("promoted_order_id, payment_status")
        .in("promoted_order_id", ids)
        .eq("payment_status", "paid");
      for (const r of promoted ?? []) {
        const pid = (r as { promoted_order_id: string | null }).promoted_order_id;
        if (pid) paidOnlineIds.add(pid);
      }
    }

    const result = orderList.map((o) => {
      const { raw, ...rest } = o;
      const derived = deriveLegacyOrderFields(rest, raw);
      const deliveredAt =
        (typeof raw?.delivered_at === "string" && raw.delivered_at) ||
        (typeof raw?.date_delivered === "string" && raw.date_delivered) ||
        (typeof raw?.delivery_date === "string" && raw.delivery_date) ||
        null;
      return {
        ...rest,
        subtotal: derived.subtotal,
        shipping_cost: derived.shipping_cost,
        payment_method: displayedPaymentMethod(derived.payment_method, raw),
        order_number:
          // Ο κωδικός που βλέπει το ops/παραστατικό (external_code) υπερισχύει,
          // ώστε πελάτης και κατάστημα να μιλούν για τον ίδιο αριθμό.
          o.external_code ??
          raw?.order_number ??
          (typeof raw?.reference === "string" ? raw.reference : null),
        payment_status: paidOnlineIds.has(o.id) ? "paid" : derived.payment_status,
        delivered_at: deliveredAt,
        items: itemsByOrder[o.id] ?? [],
      };
    });
    return { orders: JSON.parse(JSON.stringify(result)) as Json[] };
  });

function normalizeAddress(
  raw: Record<string, unknown> | null,
  fallbackName: string,
  fallbackPhone: string,
): SavedAddress | null {
  if (!raw || typeof raw !== "object") return null;
  const s = (v: unknown) => (typeof v === "string" ? v.trim() : "");
  // New eshop orders use line1/line2/city/postalCode; imported PrestaShop
  // orders use address1/address2/city/postcode + firstname/lastname.
  const line1 = s(raw.line1) || s(raw.address1);
  const line2 = s(raw.line2) || s(raw.address2);
  const city = s(raw.city);
  const postalCode = s(raw.postalCode) || s(raw.postcode);
  const composedName = [s(raw.firstname), s(raw.lastname)].filter(Boolean).join(" ").trim();
  const fullName = composedName || s(fallbackName);
  const phone = (s(raw.phone_mobile) || s(raw.phone) || s(fallbackPhone))
    .replace(/\D/g, "")
    .slice(0, 10);
  if (!line1 || !city || !postalCode) return null;
  return { fullName, phone, line1, line2, city, postalCode };
}

export const listMyAddresses = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { userId, claims } = context;

    const { data: customer } = await supabaseAdmin
      .from("customers")
      .select("id, email, full_name, phone")
      .eq("auth_user_id", userId)
      .maybeSingle();
    const email = ((customer?.email as string | undefined) ?? (claims?.email as string | undefined))
      ?.trim()
      .toLowerCase();

    const customerIds = new Set<string>();
    if (customer?.id) customerIds.add(customer.id as string);
    if (email) {
      const { data: sameEmail } = await supabaseAdmin
        .from("customers")
        .select("id")
        .ilike("email", email);
      for (const c of sameEmail ?? []) customerIds.add(c.id as string);
    }

    const orParts: string[] = [];
    if (customerIds.size > 0) {
      orParts.push(`customer_id.in.(${Array.from(customerIds).join(",")})`);
    }
    if (email) {
      orParts.push(`customer_email.ilike.${email}`);
      orParts.push(`raw->>customer_email.ilike.${email}`);
    }
    const profilePhone =
      typeof customer?.phone === "string" ? normalizePhoneDigits(customer.phone) : "";
    const profileName = typeof customer?.full_name === "string" ? customer.full_name : "";
    const contact = { fullName: profileName, phone: profilePhone };
    if (orParts.length === 0) return { addresses: [] as SavedAddress[], contact };

    const { data } = await supabaseAdmin
      .from("orders")
      .select("shipping_address, customer_name, customer_phone, created_at")
      .or(orParts.join(","))
      .order("created_at", { ascending: false })
      .limit(50);

    const seen = new Set<string>();
    const addresses: SavedAddress[] = [];
    for (const row of data ?? []) {
      const addr = normalizeAddress(
        (row.shipping_address as Record<string, unknown> | null) ?? null,
        (row.customer_name as string | null) ?? "",
        (row.customer_phone as string | null) ?? "",
      );
      if (!addr) continue;
      const key = `${addr.line1}|${addr.line2}|${addr.city}|${addr.postalCode}`.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      addresses.push(addr);
      if (addresses.length >= 8) break;
    }
    return { addresses, contact };
  });
